import { useEffect, useMemo, useState } from 'react'
import { Box, IconButton, Typography, styled, useMediaQuery, useTheme } from '@mui/material'
import SVG from 'react-inlinesvg'
import { useLocation, useNavigate } from 'react-router-dom'
import { DiscoverTabPaths, WorkspaceTabPaths, ProjectTabPaths, HOME_PATH } from '@/utils/constants'

import TabArrow from '@/assets/svgs/tab-arrow.svg'
import TabArrowBlack from '@/assets/svgs/tab-arrow-black.svg'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import { ModernCreateModal } from '@/components'
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material'
import colors from '@/styles/colors'
import { SVGS } from '@/assets/svgs'

const sidebarWidths = {
  collapsed: '88px', // Narrow icon-only state
  expanded: '240px', // Full expanded state with labels
}

const SideBarContainer = styled(Box)(({ theme }) => ({
  position: 'relative', // Changed from fixed to relative since Drawer handles positioning
  width: '100%', // Use full width of the Drawer container
  height: '100%',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxSizing: 'border-box',
  backgroundColor: '#1d1d1d',
  borderRight: 'none',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 10, // Higher z-index to be above background
}))

export const BoxWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 20,
}))

export const BottomButtonContainer = styled(Box)(() => ({
  marginTop: 'auto',
}))

export const SidebarCreateButton = styled(IconButton)(({ theme }) => ({
  background: 'linear-gradient(135deg, #5258e4 0%, #6366f1 100%)',
  color: '#ffffff',
  fontSize: '13px',
  fontWeight: 500,
  borderRadius: '12px',
  height: '40px',
  width: '100%',
  padding: '8px 16px',
  margin: '0 0 16px 0',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  border: 'none',
  fontFamily: 'Inter, sans-serif',
  boxShadow: '0 2px 8px rgba(82, 88, 228, 0.3)',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',

  '&:hover': {
    background: 'linear-gradient(135deg, #4338ca 0%, #5b21b6 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 16px rgba(82, 88, 228, 0.4)',
  },

  '&:active': {
    transform: 'translateY(0px)',
    background: 'linear-gradient(135deg, #3730a3 0%, #4c1d95 100%)',
    boxShadow: '0 2px 8px rgba(82, 88, 228, 0.3)',
  },

  '&.collapsed': {
    width: '100%',
    maxWidth: '56px',
    padding: '8px 4px',
    margin: '0 auto 16px auto',
    flexDirection: 'column',
    gap: '4px',
    '& span': {
      fontSize: '9px',
      lineHeight: 1.1,
      textAlign: 'center',
      wordBreak: 'break-word',
    },
  },
}))

export const StyledMenu = styled('div')(() => ({
  textDecoration: 'none',
  width: '100%',
}))

export const CollapseButton = styled('div')(() => ({
  position: 'absolute',
  top: 17,
  zIndex: 20,
  height: 40,
  width: 20,
  display: 'flex',
  backgroundColor: '#FFFFFF4D',
  borderRadius: '12px',
  alignItems: 'center',
  paddingLeft: 2,
  transition: 'left 0.3s',
  '&.expanded': {
    left: 254,
  },
  '&.collapsed': {
    left: 80,
  },
}))

export const TabButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  position: 'relative',
  height: 49,
  fontSize: 15,
  borderRadius: 6,
  display: 'flex',
  gap: 12,
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  padding: '8px 12px',
  margin: '2px 0',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  backgroundColor: 'transparent',
  '& svg': {
    width: '20px',
    height: '20px',
  },
  '& span': {
    fontSize: '15px',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'white',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  '&.highlighted': {
    background: 'linear-gradient(135deg, #5258e4 0%, #6366f1 100%)',
    color: '#ffffff',
    boxShadow: '0 2px 8px rgba(82, 88, 228, 0.4)',
    '& svg': {
      filter: 'brightness(0) invert(1)',
    },
    '& span': {
      color: '#ffffff',
      fontWeight: 600,
    },
    '&:hover': {
      background: 'linear-gradient(135deg, #4338ca 0%, #5b21b6 100%)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(82, 88, 228, 0.5)',
    },
  },
  // Collapsed state styling
  '&.collapsed': {
    flexDirection: 'column',
    gap: 4,
    justifyContent: 'center',
    width: '100%',
    maxWidth: '56px',
    padding: '8px 4px',
    margin: '4px auto',
    '& svg': {
      width: '20px',
      height: '20px',
    },
    '& span': {
      fontSize: '9px',
      maxWidth: '52px',
      textAlign: 'center',
      lineHeight: 1.1,
      wordBreak: 'break-word',
      color: 'white',
    },
  },
}))

export const AddWorkspaceButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  height: 64,
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  borderRadius: 14,
  display: 'flex',
  gap: 14,
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
  border: '2px solid rgba(99, 102, 241, 0.3)',
  padding: '16px 20px',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
    transition: 'left 0.5s ease',
  },
  '&:hover': {
    background:
      'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(139, 92, 246, 0.25) 100%)',
    borderColor: 'rgba(99, 102, 241, 0.5)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
    '&::before': {
      left: '100%',
    },
  },
  '&:active': {
    transform: 'translateY(0px) scale(0.98)',
  },
  '&.collapsed': {
    width: 56,
    height: 56,
    padding: 0,
    borderRadius: 14,
    justifyContent: 'center',
    '& span': {
      display: 'none',
    },
  },
}))

export default function DashboardSideBar() {
  const theme = useTheme()
  const lessThanSmall = useMediaQuery(theme.breakpoints.down('md'))

  const {
    setSideTabKey,
    setSidebarCollapsed,
    setSideTitle,
    setSideTabPath,
    setShowSideMenuDrawer,
  } = useAppStore()
  const [sideTabKey, sideTabPath, sideTitle, sidebarCollapsed, showSideMenuDrawer] = useAppStore(
    (state) => [
      state.app.sideTabKey,
      state.app.sideTabPath,
      state.app.sideTitle,
      state.home.sidebarCollapsed,
      state.app.showSideMenuDrawer,
    ],
    shallow,
  )
  const navigate = useNavigate()
  const [createNewModal, setCreateNewModal] = useState(false)
  const { length, [length - 1]: lastWorkspaceTab } = WorkspaceTabPaths

  const toggleModal = () => {
    setCreateNewModal(!createNewModal)
  }

  useEffect(() => {
    if (lessThanSmall) {
      setSidebarCollapsed(false)
    }
  }, [lessThanSmall])

  const handleNavigate = (path: string, key: string, title: string) => {
    if (key != sideTabKey) {
      setSideTabKey(key)
      setSideTitle(title)
      setSideTabPath(path)
    }
    setShowSideMenuDrawer(false)
    navigate(path)
  }

  const onActiveTabChanged = (path: string, key: string, title: string) => {
    if (key != sideTabKey) {
      setSideTabKey(key)
      setSideTitle(title)
      setSideTabPath(path)
    }
  }

  return (
    <>
      <SideBarContainer flex={1}>
        <Box p='8px' display='flex' flexDirection='column' height='100%'>
          {/* Scrollable Navigation Content */}
          <Box
            flex={1}
            sx={{
              overflowX: 'hidden',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: sidebarCollapsed ? 'center' : 'flex-start',
              minHeight: 0, // Allow flex child to shrink
            }}
          >
            {/* Create New Button - Commented out for now */}
            {/* <SidebarCreateButton
              className={sidebarCollapsed ? 'collapsed' : ''}
              onClick={toggleModal}
            >
              <SVG
                src={SVGS.TabPlus}
                width={sidebarCollapsed ? 16 : 18}
                height={sidebarCollapsed ? 16 : 18}
              />
              <span>{sidebarCollapsed ? 'New' : '+ Create New'}</span>
            </SidebarCreateButton> */}

            <Section
              title='DISCOVER'
              tabPaths={DiscoverTabPaths}
              onNavigate={handleNavigate}
              onActiveTabChanged={onActiveTabChanged}
              isCollapsed={sidebarCollapsed}
            />

            <Section
              title='MY WORKSPACE'
              tabPaths={WorkspaceTabPaths.slice(0, -1)}
              onNavigate={handleNavigate}
              onActiveTabChanged={onActiveTabChanged}
              isCollapsed={sidebarCollapsed}
            />
            {/* <Box sx={{ my: 1, backgroundColor: 'transparent' }}></Box>
              <Section
                title='PROJECTS'
                tabPaths={ProjectTabPaths}
                onNavigate={handleNavigate}
                onActiveTabChanged={onActiveTabChanged}
                isCollapsed={sidebarCollapsed}
                hasSubItems={false}
              /> */}

            {/* Bottom Create New Button - Part of navigation flow */}
            <Box
              sx={{
                marginTop: 'auto',
                padding: '16px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <StyledMenu onClick={toggleModal}>
                <AddWorkspaceButton className={sidebarCollapsed ? 'collapsed' : ''}>
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: '-4px',
                        borderRadius: '50%',
                        padding: '2px',
                        background:
                          'linear-gradient(125deg, rgba(233, 30, 99, 1.0) 0%, rgba(92, 107, 192, 1.0) 52%, rgba(25, 118, 210, 1.0) 100%)',
                        // WebkitMask:
                        //   'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                      },
                    }}
                  >
                    <SVG
                      src={lastWorkspaceTab.icon}
                      width={sidebarCollapsed ? 24 : 32}
                      height={sidebarCollapsed ? 24 : 32}
                      style={{
                        filter: 'drop-shadow(0 2px 8px rgba(233, 30, 99, 0.3))',
                      }}
                    />
                  </Box>
                  {!sidebarCollapsed && <span>{lastWorkspaceTab.title}</span>}
                </AddWorkspaceButton>
              </StyledMenu>
            </Box>
          </Box>
        </Box>
      </SideBarContainer>
      <ModernCreateModal
        open={createNewModal}
        onClose={() => {
          setCreateNewModal(false)
        }}
      />
    </>
  )
}

function Section({
  title,
  tabPaths,
  onActiveTabChanged,
  onNavigate,
  isCollapsed,
  hasSubItems = false,
}: {
  title: string
  tabPaths: typeof DiscoverTabPaths
  onActiveTabChanged: (path: string, key: string, title: string) => void
  onNavigate: (path: string, key: string, title: string) => void
  isCollapsed: boolean
  hasSubItems?: boolean
}) {
  const location = useLocation()
  const [isSubMenuExpanded, setIsSubMenuExpanded] = useState(true)
  const isActiveRoute = (tabPath: string, tabKey: string, tabTitle: string) => {
    // More precise route matching
    const currentPath = location.pathname
    let flag = false

    if (tabPath === '/discover/home' || tabPath === '/home') {
      // Home should only be active for exact home paths
      flag = currentPath === '/' || currentPath === '/home' || currentPath === '/discover/home'
    } else {
      // For other routes, check if current path starts with the tab path
      flag = currentPath.startsWith(tabPath) && currentPath !== '/'
    }

    if (flag) {
      onActiveTabChanged(tabPath, tabKey, tabTitle)
    }
    return flag
  }

  const renderMenuItems = () => (
    <>
      {tabPaths.map((tab) => (
        <StyledMenu key={tab.key} onClick={() => onNavigate(tab.path, tab.key, tab.title)}>
          <TabButton
            sx={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}
            className={`${isActiveRoute(tab.path, tab.key, tab.title) ? 'highlighted' : ''} ${
              isCollapsed ? 'collapsed' : ''
            }`}
          >
            <SVG src={tab.icon} width={20} height={20} />
            {!isCollapsed && <span>{tab.title}</span>}
          </TabButton>
        </StyledMenu>
      ))}
    </>
  )

  const isProjectSubMenuSelected = useMemo(() => {
    return location.pathname.indexOf('projects/') > -1
  }, [location.pathname])

  return (
    <>
      {!hasSubItems ? (
        <>
          {title.length > 0 && !isCollapsed && (
            <Typography
              variant='labelSmall'
              sx={{
                fontSize: 11,
                mt: 2,
                mb: 1,
                color: 'rgba(255, 255, 255, 0.4)',
                fontWeight: 500,
                letterSpacing: '0.5px',
              }}
            >
              {title}
            </Typography>
          )}
          {renderMenuItems()}
        </>
      ) : (
        <>
          <StyledMenu
            className={true ? 'highlighted' : ''}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              color: 'white',
              cursor: 'pointer',
              borderRadius: '8px',
              backgroundColor:
                isProjectSubMenuSelected && !isSubMenuExpanded ? colors.meshdomBlue : 'transparent',
            }}
            onClick={() => setIsSubMenuExpanded(!isSubMenuExpanded)}
          >
            <TabButton
              sx={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}
              className={false ? 'highlighted' : ''}
            >
              <SVG src={SVGS.TabPattern} width={20} height={20} />
              {!isCollapsed && <span>{title}</span>}
            </TabButton>
            {isSubMenuExpanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </StyledMenu>

          {isSubMenuExpanded && <Box sx={{ ml: '16px', width: '90%' }}>{renderMenuItems()}</Box>}
        </>
      )}
    </>
  )
}
